import { Alert, AlertTitle, Box, Paper, useTheme } from "@mui/material";
import * as React from "react";

import { EventDetails } from "@/shared/components/events/EventDetails";
import { EventRecord } from "@/shared/domain/events";

import { RobotoDataGrid } from "../RobotoDataGrid";

import { ColumnsModal } from "./ColumnsModal";
import { TablePageState, TablePageAction } from "./stateReducer";
import { createRows, retrieveMetadataColumns } from "./tableLogic";
import type { BaseTableProps } from "./tableProps";

const doNothing = () => {};

const possibleStandardEventColumns = [
  "event_id",
  "name",
  "description",
  "start_time",
  "end_time",
  "created",
  "created_by",
  "modified",
  "modified_by",
  "tags",
];

const sortableColumns = new Set<string>();

interface EventResultsTableProps extends BaseTableProps<EventRecord> {
  eventsTableState: TablePageState;
  eventsTableDispatch: React.Dispatch<TablePageAction>;
}

export const EventResultsTable: React.FC<EventResultsTableProps> = ({
  data: events,
  dataCount,
  loading,
  containerStyle,
  fetchMoreData,
  eventsTableState: state,
  eventsTableDispatch: dispatch,
}) => {
  const theme = useTheme();

  const [page, setPage] = React.useState(0);

  const eventsById = new Map<string, EventRecord>(
    events.map((event) => {
      return [event.event_id, event];
    }),
  );

  const pageData = React.useMemo(() => {
    return events.slice(
      page * state.rowsPerPage,
      page * state.rowsPerPage + state.rowsPerPage,
    );
  }, [events, state.rowsPerPage, page]);

  const { rows, allPossibleColumns } = React.useMemo(() => {
    const possibleMetadataColumns: string[] =
      retrieveMetadataColumns(pageData) ?? [];

    const rows = createRows(pageData, state.columns, possibleMetadataColumns);

    const allPossibleColumns = possibleStandardEventColumns
      .concat(possibleMetadataColumns)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

    return { rows, allPossibleColumns };
  }, [pageData, state.columns]);

  const noSearchResults =
    Object.keys(state.lastSearchQuery).length > 0 && events.length === 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
      }}
    >
      <Box
        sx={{
          flex: 1,
          maxWidth: "100%",
        }}
      >
        <Paper
          sx={{
            mt: theme.spacing(2),
          }}
        >
          {noSearchResults && (
            <Alert severity="info">
              <AlertTitle>Events</AlertTitle>
              <>
                No events match your search criteria. Try changing your search
                terms and try again.
              </>
            </Alert>
          )}

          {!noSearchResults && (
            <>
              <RobotoDataGrid
                sortableColumns={sortableColumns}
                sortingOrder={state.sortOrder}
                sortingColumnIndex={state.sortColumnIndex}
                page={page}
                rowsPerPage={state.rowsPerPage}
                nextPageButtonDisabled={
                  (page + 1) * state.rowsPerPage >= events.length
                }
                loading={loading}
                containerStyle={containerStyle}
                columnNames={state.columns}
                rows={rows}
                totalRowCount={dataCount}
                onPageChange={(newPage, rowsPerPage) => {
                  if ((newPage + 2) * rowsPerPage >= events.length) {
                    // Request more pages of data from the backend
                    // as we approach the last page of cached results.
                    // 2 pages prior seemed to work well experimentally.
                    fetchMoreData?.();
                  }
                  setPage(newPage);
                }}
                onRowsPerPageChange={(_page, newRowsPerPage) => {
                  setPage(0);
                  dispatch({
                    type: "SET_TABLE_CONFIG",
                    payload: {
                      rowsPerPage: newRowsPerPage,
                    },
                  });
                }}
                onColumnSortClicked={doNothing}
                onAddColumnClick={() => {
                  dispatch({ type: "SET_MODAL_OPEN", payload: true });
                }}
                onRowSingleClick={doNothing}
                onRowDoubleClick={doNothing}
                onSelectedRowsChange={doNothing}
                isRowSelectable={false}
                isRowExpandable={true}
                clearSelectedRowsToggle={false}
                expandableContent={(rowId: string) => {
                  const event = eventsById.get(rowId);
                  if (event) {
                    return (
                      <Box
                        sx={{
                          padding: theme.spacing(3, 2),
                        }}
                      >
                        <EventDetails event={event} readonly={true} />
                      </Box>
                    );
                  }
                }}
              />

              <ColumnsModal
                open={state.modalOpen}
                handleClose={() => {
                  dispatch({ type: "SET_MODAL_OPEN", payload: false });
                }}
                columns={allPossibleColumns}
                currentColumns={state.columns}
                onSave={(updatedColumns) => {
                  dispatch({ type: "SAVE_COLUMNS", payload: updatedColumns });
                }}
              />
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};
